import { PROGRAMS } from '../constants/programs'

// A lead is the only record in this portal that goes cold on its own. An
// invoice, a task, a project all have somebody on the other end who chases
// them. A lead just sits. `/leads` had exactly one signal for that — a
// follow-up date somebody had to remember to type — so a lead nobody dated was
// indistinguishable from a lead that was going fine. The single live lead sat
// 131 days past the date it *did* have and nothing anywhere said so.
//
// So the decision "what is happening with this lead" is made here, once, and
// the page renders it. Same shape as `invoiceStage()` in paymentPipeline.js.

/** Silence this long on an open lead and it is cold, dated or not. */
export const COLD_AFTER_DAYS = 14

export const LEAD_STAGES = [
  { key: 'new', label: 'New', color: '#1D4ED8', bg: 'rgba(29,78,216,0.08)' },
  { key: 'contacted', label: 'Contacted', color: '#854D0E', bg: '#FEF9C3' },
  { key: 'qualified', label: 'Qualified', color: '#059669', bg: 'rgba(5,150,105,0.08)' },
  { key: 'closed/won', label: 'Won', color: '#0F766E', bg: 'rgba(13,148,136,0.08)' },
  { key: 'closed/lost', label: 'Lost', color: '#DC2626', bg: 'rgba(220,38,38,0.08)' },
]

export const OPEN_STAGES = ['new', 'contacted', 'qualified']

// `leads.source` has always been stored and has never been shown. Four writers
// put four different values in it — the quote form, the networking function,
// the Add Lead modal (via the column default), and nothing yet for referrals —
// and the page rendered none of them, so "where did this come from" had no
// answer on screen.
export const LEAD_SOURCES = {
  'quote-form': { key: 'quote-form', label: 'Quote form', short: 'Quote', color: '#1D4ED8', bg: 'rgba(29,78,216,0.10)' },
  networking: { key: 'networking', label: 'Networking', short: 'Met', color: '#7E22CE', bg: 'rgba(126,34,206,0.10)' },
  referral: { key: 'referral', label: 'Referral', short: 'Referral', color: '#059669', bg: 'rgba(5,150,105,0.10)' },
  portal: { key: 'portal', label: 'Added by hand', short: 'By hand', color: '#888780', bg: 'rgba(136,135,128,0.12)' },
  ghl: { key: 'ghl', label: 'GoHighLevel', short: 'GHL', color: '#0F766E', bg: 'rgba(15,118,110,0.10)' },
}

const UNKNOWN_SOURCE = { key: 'unknown', label: 'Unknown source', short: 'Unknown', color: '#888780', bg: 'rgba(136,135,128,0.12)' }

export function leadSource(lead) {
  return LEAD_SOURCES[lead?.source] || UNKNOWN_SOURCE
}

export const SOURCE_KEYS = ['quote-form', 'networking', 'referral', 'ghl', 'portal']

export function isOpen(lead) {
  return OPEN_STAGES.includes(lead?.status || 'new')
}

export function ymd(date) {
  return new Date(date).toISOString().slice(0, 10)
}

/**
 * The most recent thing that actually happened to this lead. Anything that
 * counts as contact counts here — a note, the intro email, a reply relayed by
 * the GoHighLevel webhook — because a lead you emailed yesterday is not cold
 * just because it was created a month ago.
 */
export function lastTouchAt(lead) {
  if (!lead) return null
  const candidates = [
    lead.created_at,
    lead.intro_sent_at,
    lead.replied_at,
    lead.appointment_at,
    lead.last_event_at,
    ...(Array.isArray(lead.notes_list) ? lead.notes_list.map(n => n && n.at) : []),
  ]
  let best = null
  for (const c of candidates) {
    if (!c) continue
    const t = new Date(c).getTime()
    if (Number.isNaN(t)) continue
    if (best === null || t > best) best = t
  }
  return best === null ? null : new Date(best).toISOString()
}

export function daysSince(iso, now = new Date()) {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  return Math.floor((now.getTime() - then) / 86400000)
}

/**
 * One decision per lead. `key` is what the page colours by, `days` is the
 * number worth showing next to it.
 *
 * - overdue        a follow-up date that has passed
 * - today          a follow-up date that is today
 * - scheduled      a follow-up date in the future — nothing to do
 * - cold           no date, and nothing has happened for COLD_AFTER_DAYS
 * - no-next-action no date, but recent enough that it is not yet a problem
 * - closed         won or lost; never nags
 */
export function leadAttention(lead, now = new Date()) {
  if (!isOpen(lead)) return { key: 'closed', label: 'Closed', days: null }

  const today = ymd(now)
  const due = lead?.follow_up_date || null

  if (due && due < today) {
    return { key: 'overdue', label: 'Overdue', days: daysSince(`${due}T00:00:00.000Z`, now) }
  }
  if (due && due === today) return { key: 'today', label: 'Due today', days: 0 }
  if (due) return { key: 'scheduled', label: 'Scheduled', days: null }

  const idle = daysSince(lastTouchAt(lead), now)
  if (idle !== null && idle >= COLD_AFTER_DAYS) {
    return { key: 'cold', label: 'Cold', days: idle }
  }
  return { key: 'no-next-action', label: 'No next action', days: idle }
}

export const ATTENTION_STYLES = {
  overdue: { color: '#EF4444', bg: '#FEF2F2', border: '#FCA5A5' },
  today: { color: '#D97706', bg: '#FFFBEB', border: '#FCD34D' },
  cold: { color: '#7E22CE', bg: 'rgba(126,34,206,0.07)', border: 'rgba(126,34,206,0.35)' },
  'no-next-action': { color: '#888780', bg: 'var(--off)', border: 'var(--bdr2)' },
  scheduled: { color: '#888780', bg: 'var(--off)', border: 'var(--bdr2)' },
  closed: { color: '#888780', bg: 'var(--off)', border: 'var(--bdr2)' },
}

/**
 * Counts for the filter chips. Every chip's number comes from here so a chip
 * cannot say 3 and then show 5 rows — the Overdue chip on the invoices page
 * counted a status nothing ever wrote, and read 0 forever.
 */
export function summarizeLeads(leads = [], now = new Date()) {
  const out = {
    all: leads.length,
    new: 0, active: 0, closed: 0,
    overdue: 0, today: 0, cold: 0, noNextAction: 0, needsAttention: 0,
  }
  for (const lead of leads) {
    const status = lead.status || 'new'
    if (status === 'new') out.new += 1
    if (status === 'contacted' || status === 'qualified') out.active += 1
    if (status === 'closed/won' || status === 'closed/lost') out.closed += 1

    const attention = leadAttention(lead, now)
    if (attention.key === 'overdue') out.overdue += 1
    if (attention.key === 'today') out.today += 1
    if (attention.key === 'cold') out.cold += 1
    if (attention.key === 'no-next-action') out.noNextAction += 1
    if (attention.key === 'overdue' || attention.key === 'today' || attention.key === 'cold') {
      out.needsAttention += 1
    }
  }
  return out
}

export function countBySource(leads = []) {
  const out = {}
  for (const lead of leads) {
    const key = leadSource(lead).key
    out[key] = (out[key] || 0) + 1
  }
  return out
}

// ── Programs ─────────────────────────────────────────────────────────────────

/**
 * Budget is free text everywhere it is written — "14,000" from the portal,
 * "$10,000–$25,000" and "Under $10,000" from the quote form's bands. Returns
 * the number the band is *anchored* on, and whether the band caps below it.
 */
export function parseBudget(raw) {
  if (raw === 0) return { amount: 0, below: false }
  if (!raw) return null
  const text = String(raw)
  const below = /\bunder\b|\bless than\b|\bup to\b|\bbelow\b/i.test(text)
  const numbers = (text.match(/\d[\d,]*/g) || []).map(n => Number(n.replace(/,/g, '')))
  if (numbers.length === 0) return null
  // The floor of a band, not its ceiling. "$10,000–$25,000" is a prospect
  // saying "at least ten"; reading it as twenty-five overstates the lead.
  const amount = Math.min(...numbers)
  return { amount, below }
}

/**
 * A service named on its own — typed by hand, or written before the quote form
 * added the program — and the program it belongs to. The names are the price
 * list's (migration 0071); read before the older words below, so "Visual
 * Identity System" is identity work and not the old Identity phase.
 */
const SERVICE_SCOPE = [
  { words: ['positioning', 'messaging', 'brand architecture'], program: 'brand-strategy' },
  { words: ['visual identity', 'brand voice', 'guidelines', 'templates & rollout'], program: 'brand-identity' },
  { words: ['website', 'crm', 'marketing automation', 'funnel', 'campaign'], program: 'website-growth' },
  { words: ['fractional cmo', 'brand system maintenance'], program: 'marketing-leadership' },
]

/**
 * Words a lead written before the price list may carry, and the program they
 * meant. Read only when a lead names no program outright — "Brand Identity"
 * contains "identity", and the ICON phase called Identity was strategy work.
 */
const LEGACY_SCOPE = [
  { words: ['icon build', 'optimization'], program: 'website-growth' },
  { words: ['navigation', 'growth partnership'], program: 'marketing-leadership' },
  { words: ['communication'], program: 'brand-identity' },
  { words: ['brand foundation', 'identity'], program: 'brand-strategy' },
]

/**
 * Which program this lead is pointed at, so a qualified lead on the board says
 * what kind of conversation it is.
 *
 * The quote form records each choice as "Program: Service", so the program is
 * named in the lead itself. When several are asked for, the furthest along
 * wins: somebody asking for a website and positioning is starting a website
 * engagement, with the positioning that has to come first inside it.
 *
 * Scope only. Under ICON a budget pointed at one of two fixed packages; the
 * price list is not a ladder of packages, and naming a program from a number
 * alone would be a guess dressed as a finding. The board shows the budget.
 *
 * Returns { key, name, basis } or null when the lead names no program.
 */
export function impliedProgram(lead) {
  const asked = String(lead?.project_type || '').toLowerCase()
  if (!asked) return null

  const byWords = (table) => {
    const keys = new Set(table.filter(l => l.words.some(w => asked.includes(w))).map(l => l.program))
    return PROGRAMS.filter(p => keys.has(p.key))
  }
  let named = PROGRAMS.filter(p => asked.includes(p.title.toLowerCase()))
  if (!named.length) named = byWords(SERVICE_SCOPE)
  if (!named.length) named = byWords(LEGACY_SCOPE)
  if (!named.length) return null

  const furthest = named[named.length - 1]
  return { key: furthest.key, name: furthest.title, basis: 'scope' }
}

/** Options for the Add Lead form, in the same vocabulary the quote form uses. */
export const PROJECT_TYPE_OPTIONS = [
  ...PROGRAMS.map(p => p.title),
  'Not sure yet',
]

/**
 * The proposal a lead should become.
 *
 * A lead could not be sent a proposal at all until `proposals.lead_id` existed
 * (migration 0027): `client_id` was the only attachment point, so quoting
 * anybody meant first creating a client record for someone who had not bought.
 * That made "Won" the pipeline's only honest end state and left a client row
 * behind for every deal that went nowhere.
 *
 * The lead's program seeds the title, so the studio opens on the engagement
 * the lead already points at rather than on "New Proposal". Nothing is priced
 * here — lines come from the price list in the studio.
 */
export function proposalDraftFromLead(lead, { proposalNumber, token }) {
  const pkg = impliedProgram(lead)
  const who = lead?.company || lead?.name || 'New prospect'
  return {
    proposal_type: 'rich',
    token,
    proposal_number: proposalNumber,
    // Attached to the lead, not to a client that does not exist yet.
    lead_id: lead?.id || null,
    client_id: null,
    title: pkg ? `${pkg.name} — ${who}` : `Proposal — ${who}`,
    // service_tier is the retired ICON package label; a proposal's services
    // are its investment lines now.
    service_tier: null,
    status: 'draft',
    content: { sections: [] },
    total: 0,
    validity_days: 30,
  }
}
